/**
 * Guards React's DOM mutations against third-party scripts that rewrite the
 * DOM behind React's back: Chrome/Safari page translation ("Google Translate")
 * and some browser extensions replace text nodes with their own elements.
 * When React later tries to remove or insert around a node it no longer owns,
 * the browser throws `NotFoundError: Failed to execute 'removeChild' on 'Node'`
 * and the whole tree falls into the ErrorBoundary. The legacy portal hit this
 * in production the moment users pressed "Sign in" (the label is swapped for a
 * spinner); the login, verification and transfer-confirmation buttons here do
 * the same swap, so the guard is installed before React mounts.
 *
 * Ported from the legacy portal hotfix (main cb41a5d).
 * See https://github.com/facebook/react/issues/11538
 */

const GUARD_FLAG = '__triniDomMutationGuard__';

type GuardedNodePrototype = Node & Record<typeof GUARD_FLAG, boolean | undefined>;

export const installDomMutationGuard = (): void => {
  if (typeof Node !== 'function' || Node.prototype === undefined) return;

  const prototype = Node.prototype as GuardedNodePrototype;
  if (prototype[GUARD_FLAG] === true) return;
  prototype[GUARD_FLAG] = true;

  // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked with an explicit `this` via .call
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode === this) {
      return originalRemoveChild.call(this, child) as T;
    }

    // The node was moved (for example wrapped by a page translator): drop it
    // from wherever it lives now, which is what the caller intended.
    if (child.parentNode !== null) {
      return originalRemoveChild.call(child.parentNode, child) as T;
    }

    return child;
  };

  // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked with an explicit `this` via .call
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode === null || referenceNode.parentNode === this) {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    }

    return originalInsertBefore.call(this, newNode, null) as T;
  };
};
